import type { TenantSession } from './session'
import type { RosterPermit, RosterVehicle } from './api'

const WELCOME_KEY_PREFIX = 'parkify_tenant_welcome_v2'

export interface TenantWelcomeRosterSummary {
  parkerCount: number
  plateCount: number
  permitCount: number
  programCount: number
}

/**
 * The welcome uses the same live roster projection as Parkers and Permits.
 * Multiple plates with the same person detail on one permit count as one
 * parker, while plate-only legacy rows remain individually countable.
 */
export function summarizeTenantWelcomeRoster(permits: readonly RosterPermit[]): TenantWelcomeRosterSummary {
  const live = permits.filter(permit => permit.status !== 'expired')
  return {
    parkerCount: countRosterParkers(live),
    plateCount: live.reduce((sum, permit) => sum + permit.vehicles.length, 0),
    permitCount: live.length,
    programCount: new Set(live.map(permit => permit.permitType || 'employee')).size,
  }
}

/**
 * The key one vehicle's PARKER is grouped by: two vehicles with the same key
 * are one person. Mirror of `parkerKey` in api/src/permits/tenant_pool.ts (the
 * rule seats, the Parkers rows and the console use); keep the two identical, or
 * this portal shows a different number of people than the tenant is billed for.
 */
export function parkerIdentity(v: Pick<RosterVehicle, 'plate' | 'driver' | 'email' | 'phone' | 'holderId'>): string {
  const holder = v.holderId?.trim()
  if (holder) return `h:${holder}`
  const driver = v.driver?.trim().toLowerCase().replace(/\s+/g, ' ')
  if (driver) return `d:${driver}`
  const email = v.email?.trim().toLowerCase()
  if (email) return `e:${email}`
  const phone = v.phone?.replace(/\D/g, '')
  if (phone) return `p:${phone}`
  return `pl:${v.plate}`
}

/**
 * People on these permit records: one per person on each permit, however many
 * plates they hold. A pooled permit is one record holding many people (one
 * seat per person, OWNER-DECISIONS 2026-10-10), so `permits.length` is not a
 * head count. Parkers' headline and each Permits card both count with this,
 * so their numbers cannot drift apart. Liveness is the caller's filter.
 */
export function countRosterParkers(permits: readonly RosterPermit[]): number {
  const parkers = new Set<string>()
  for (const permit of permits) {
    for (const vehicle of permit.vehicles) parkers.add(`${permit.id}:${parkerIdentity(vehicle)}`)
  }
  return parkers.size
}

/**
 * One acknowledgement per tenant + signed-in person on this browser.
 *
 * Tenant managers commonly share a computer, and one person dismissing the
 * introduction must not suppress it for every other manager who signs in on
 * that device. The session token never enters the key.
 */
export function tenantWelcomeKey(session: Pick<TenantSession, 'tenantId' | 'email'>): string {
  return `${WELCOME_KEY_PREFIX}:${session.tenantId}:${session.email.trim().toLowerCase()}`
}

export function shouldShowTenantWelcome(
  session: TenantSession,
  storage: Pick<Storage, 'getItem'> | null = browserStorage(),
): boolean {
  if (session.kind === 'device') return false
  if (!storage) return true
  try {
    return storage.getItem(tenantWelcomeKey(session)) !== 'dismissed'
  } catch {
    // Private browsing can make Storage throw. The welcome is still useful in
    // the current session; dismissing it closes local React state either way.
    return true
  }
}

export function dismissTenantWelcome(
  session: TenantSession,
  storage: Pick<Storage, 'setItem'> | null = browserStorage(),
): void {
  if (!storage || session.kind === 'device') return
  try { storage.setItem(tenantWelcomeKey(session), 'dismissed') } catch { /* private mode */ }
}

function browserStorage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage } catch { return null }
}

/**
 * Under a read-only view-as lens the welcome is display-only: its dismissal
 * lives in this tab's sessionStorage, so the staffer sees it once per lens and
 * nothing durable is recorded against the target on this browser.
 */
export function welcomeStorageFor(session: TenantSession): Storage | null {
  if (!session.viewAs) return browserStorage()
  try { return typeof window === 'undefined' ? null : window.sessionStorage } catch { return null }
}
